<template>
  <div>
    <form-viewer-field
      v-for="(field, index) in fieldsNotAssignedToSteps"
      :key="index"
      v-model="internalValue[field.model]"
      :field="field"
    >
      <!-- passthrough slots -->
      <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot :name="name" v-bind="slotData" />
      </template>
    </form-viewer-field>

    <v-stepper v-if="isStepper" v-model="stepper" vertical>
      <template v-for="(step, index) in steps">
        <ValidationObserver
          ref="observer"
          v-slot="{ failed }"
          :key="index"
          tag="div"
        >
          <v-stepper-step
            :step="index + 1"
            :editable="stepper > index + 1"
            :complete="stepper > index + 1"
            :rules="[() => !failed]"
          >
            {{ step.label }}
          </v-stepper-step>

          <v-stepper-content :step="index + 1">
            <form-viewer-field
              v-for="(field, i) in step.fields"
              :key="i"
              v-model="internalValue[field.model]"
              :field="field"
            >
              <!-- passthrough slots -->
              <slot v-for="(_, name) in $slots" :slot="name" :name="name" />
              <template
                v-for="(_, name) in $scopedSlots"
                :slot="name"
                slot-scope="slotData"
              >
                <slot :name="name" v-bind="slotData" />
              </template>
            </form-viewer-field>

            <v-btn
              v-if="index + 1 < steps.length"
              color="primary"
              class="mt-4"
              @click="nextStep"
            >
              {{ $t('fresh-vue-forms.FormViewer.action-next') }}
            </v-btn>
          </v-stepper-content>
        </ValidationObserver>
      </template>
    </v-stepper>
  </div>
</template>

<script>
import FormViewerField from './FormViewerField'

export default {
  name: 'FormViewer',

  components: {
    FormViewerField
  },

  props: {
    value: {
      type: Object,
      required: false,
      default: () => {}
    },

    /**
     * Structure:
     * {
     *  fields: [
     *    {
     *      type: 'text',
     *      label: 'some_label_text',
     *      model: 'some_unique_name',
     *      rules: 'required|email',
     *      prependIcon: 'mdi-email',
     *      disabled: false,
     *      loading: false
     *    },
     *    {
     *      type: 'select',
     *      label: 'some_label_text',
     *      model: 'some_unique_name',
     *      items: [
     *        {
     *          value: 0
     *          text: 'Select 0'
     *        }
     *      ],
     *      rules: 'required',
     *      prependIcon: 'mdi-pencil',
     *      disabled: false,
     *      loading: false
     *    },
     *  ]
     * }
     */
    schema: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      internalValue: {},
      stepper: 1
    }
  },

  computed: {
    isStepper() {
      return this.steps?.length > 0
    },

    steps() {
      var _return = []
      var step = null

      // 1. Loop over every field
      this.schema?.fields?.forEach((field) => {
        if (field.type === 'step') {
          // 2. If the field is a "step", we need to return the last step and create a new one
          _return.push(step)
          step = {
            label: field.label,
            fields: []
          }
        } else {
          // 3. If the field is not a step, we need to add it to the last step
          step?.fields?.push(field)
        }
      })
      // 4. Add the last step
      _return.push(step)

      return _return?.filter(Boolean)
    },

    fieldsNotAssignedToSteps() {
      if (!this.isStepper) {
        return this.schema?.fields ?? []
      } else {
        var _return = []

        for (let i = 0; i < this.schema?.fields?.length ?? 0; i++) {
          let field = this.schema.fields[i]
          if (field.type !== 'step') {
            _return.push(field)
          } else {
            break
          }
        }

        return _return
      }
    }
  },

  watch: {
    value: {
      handler: function (value) {
        if (value !== this.internalValue) {
          this.internalValue = Object.assign({}, value)
        }
      },
      immediate: true
    },

    internalValue: {
      handler: function (data) {
        this.$emit('input', data)
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    nextStep() {
      this.$refs.observer?.[this.stepper - 1]?.validate().then((valid) => {
        if (valid) {
          this.stepper++
        }
      })
    }
  }
}
</script>
